// FlowLedger — one flow's scenarios grouped by kind, each group header
// teaching what the kind means. Every row shows manifest-declared structure
// (events / steps / branches) plus the verdict its last run actually earned.
// Clicking a row loads the scenario and opens the staged canvas at stage 1.

import { useDispatch, useSelector } from 'react-redux';
import { selectManifest, selectActiveSource } from '../../store/slices/scenarioSourceSlice.ts';
import { selectSuiteResults } from '../../store/slices/simulationSlice.ts';
import { switchScenario } from '../../store/sagas/scenarioSaga.ts';
import { selectViewFlowId, setAppView, openScenarioView } from '../../store/slices/uiSlice.ts';
import {
  KIND_GLYPHS,
  KIND_NAMES,
  KIND_DEFINITIONS,
  TYPE_CHIP_STYLES,
  VERDICT_CHIP_STYLES,
} from '../simulation/scenarioBadges.ts';
import {
  groupByKind,
  verdictMapFromSuite,
  tallyLine,
  flowStructureOf,
  splitVariant,
  scenarioAvailability,
} from './ledgerData.ts';
import { getCachedTopology } from '../../services/RemoteScenarioLoader.ts';
import type { RootState } from '../../store/index.ts';
import type { FlowEntry, ManifestEntry } from '../../types/index.ts';

export default function FlowLedger() {
  const dispatch = useDispatch();
  const manifest = useSelector(selectManifest);
  const activeSource = useSelector(selectActiveSource);
  const viewFlowId = useSelector(selectViewFlowId);
  const activeFlowId = useSelector(
    (s: RootState) => s.scenarioSource[s.scenarioSource.activeSource].activeFlowId,
  );
  const loadedTopologies = useSelector(
    (s: RootState) => s.scenarioSource[s.scenarioSource.activeSource].loadedTopologies,
  );
  const loadedScenarios = useSelector(
    (s: RootState) => s.scenarioSource[s.scenarioSource.activeSource].loadedScenarios,
  );
  const results = useSelector(selectSuiteResults);

  const flowId = viewFlowId ?? activeFlowId;
  const flow: FlowEntry | undefined =
    manifest?.flows.find((f) => f.flowId === flowId) ?? manifest?.flows[0];

  if (!manifest || !flow) {
    return (
      <div className="fv2-page" data-screen="flow">
        <div className="fv2-page-inner">
          <div className="fv2-kicker">FLOW LEDGER</div>
          <p className="fv2-hero-copy">No flow selected — open one from the portfolio ledger.</p>
        </div>
      </div>
    );
  }

  const verdicts = verdictMapFromSuite(results);
  const groups = groupByKind(flow, verdicts);
  const structure = flowStructureOf(
    loadedTopologies[flow.flowId] ?? getCachedTopology(flow.flowId),
  );
  const passCount = flow.scenarios.filter((e) => verdicts[e.scenarioId] === 'pass').length;
  const total = flow.scenarios.length;
  const allPass = passCount === total && total > 0;
  const happy = flow.scenarios.find((e) => e.isHappyPath);

  // For user/websocket sources the manifest may declare scenarios whose
  // files were never uploaded/received — those rows must be disabled with a
  // reason, not silently broken (the example source fetches on demand).
  const canOpen = (entry: ManifestEntry): { ok: boolean; reason?: string } =>
    scenarioAvailability(activeSource, flow, entry, loadedTopologies, loadedScenarios);

  const openScenario = (entry: ManifestEntry) => {
    dispatch(switchScenario({ source: activeSource, flow, entry }));
    dispatch(openScenarioView());
  };

  const stats: { v: string; k: string; c?: string }[] = [
    {
      v: `${passCount}/${total}`,
      k: allPass ? 'SCENARIOS PASS' : 'SCENARIOS PASSING',
      c: allPass ? 'var(--green)' : passCount === 0 ? 'var(--text-dim)' : undefined,
    },
  ];
  if (structure) {
    stats.push(
      { v: String(structure.moments), k: 'MOMENTS' },
      { v: String(structure.lanes), k: 'LANES' },
      {
        v: String(structure.branchPoints),
        k: structure.branchPoints === 1 ? 'BRANCH POINT' : 'BRANCH POINTS',
      },
      { v: String(structure.crossings), k: 'CROSSINGS' },
      { v: String(structure.terminals), k: 'TERMINALS' },
    );
  }

  return (
    <div className="fv2-page" data-screen="flow">
      <div className="fv2-page-inner">
        <div className="fv2-breadcrumb">
          <button className="fv2-crumb-link" onClick={() => dispatch(setAppView('portfolio'))}>
            Ledger
          </button>
          <span className="fv2-crumb-sep">/</span>
          {flow.flowName}
        </div>
        <h2 className="fv2-flow-title">
          {flow.flowName}{' '}
          <span className="fv2-flow-title-sub">
            FLOW · {total} SCENARIO{total === 1 ? '' : 'S'}
          </span>
        </h2>
        {happy?.description && <p className="fv2-hero-copy">{happy.description}</p>}

        <div className="fv2-stat-strip">
          {stats.map((s) => (
            <div className="fv2-stat" key={s.k}>
              <div className="fv2-stat-v" style={s.c ? { color: s.c } : undefined}>
                {s.v}
              </div>
              <div className="fv2-stat-k">{s.k}</div>
            </div>
          ))}
        </div>
        {!structure && (
          <p className="fv2-ledger-note">
            Structural stats (moments, lanes, crossings) appear once this flow&rsquo;s topology has
            loaded — run the suite or open a scenario.
          </p>
        )}

        {groups.map((g) => (
          <div className="fv2-kind-group" key={g.kind}>
            <div className="fv2-kind-head">
              <span className="fv2-kind-glyph" style={{ color: TYPE_CHIP_STYLES[g.kind].color }}>
                {KIND_GLYPHS[g.kind]}
              </span>
              <h3>{KIND_NAMES[g.kind]}</h3>
              <span className="fv2-kind-def">{KIND_DEFINITIONS[g.kind]}</span>
              <span className="fv2-kind-tally">{tallyLine(g.tally)}</span>
            </div>
            <div className="fv2-scenario-cols">
              <span>SCENARIO</span>
              <span className="fv2-num-col">EVENTS</span>
              <span className="fv2-num-col">STEPS</span>
              <span className="fv2-num-col">BRANCHES</span>
              <span className="fv2-num-col">VERDICT</span>
            </div>
            {g.entries.map((entry) => {
              const { base, variant } = splitVariant(entry.scenarioLabel);
              const v = verdicts[entry.scenarioId] ?? null;
              const chip = v !== null ? VERDICT_CHIP_STYLES[v] : null;
              const avail = canOpen(entry);
              return (
                <button
                  key={entry.scenarioId}
                  className={`fv2-scenario-row${avail.ok ? '' : ' fv2-scenario-row-disabled'}`}
                  onClick={() => avail.ok && openScenario(entry)}
                  disabled={!avail.ok}
                  title={avail.ok ? entry.description : avail.reason}
                >
                  <div className="fv2-scenario-label">
                    {base} {variant && <span className="fv2-variant-tag">{variant}</span>}
                  </div>
                  <div className="fv2-num-col fv2-mono-dim">{entry.eventCount}</div>
                  <div className="fv2-num-col fv2-mono-dim">{entry.pathLength}</div>
                  <div className="fv2-num-col fv2-mono-dim">{entry.branchCount}</div>
                  <div className="fv2-num-col">
                    {chip ? (
                      <span
                        className="fv2-verdict-chip"
                        style={{ color: chip.color, backgroundColor: chip.bg }}
                        title="Verdict from the last run"
                      >
                        {v === 'pass' ? '✓ ' : v === 'fail' ? '✗ ' : v === 'error' ? '⚠ ' : ''}
                        {chip.label}
                      </span>
                    ) : (
                      <span className="fv2-dim" title="This scenario has not run yet">
                        NOT RUN
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
